import React, {
  Dispatch, PropsWithChildren, SetStateAction, memo, useCallback, useEffect,
  useState, useMemo
} from 'react';
import { useRouteMatch, Route } from 'react-router';
import moment from 'moment';
import { Trans } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { t } from 'i18next';

import { TopNavigationBar } from '@common/components/navigation-bar/top-navigation-bar';
import Container from '@common/components/container';
import { Button } from '@common/components/button';
import Spinner from '@common/components/spinner';
import { StoreState } from '@common/types/store';
import Api, { useRequest } from '@common/services/api';
import { Select } from '@common/components/form/inputs/select';
import { AlertService } from '@common/services/alert';
import { EPredicateFilters } from '@common/components/predicates-filter';
import { EPredicateFields, EPredicateOperators, EStatus } from '@common/definitions';
import * as orgSelectors from '@modules/organisation/selectors/organisation';
import {
  EPulseSurveyStatus,
  PulseSurveyQuestionSelection,
  PulseSurveyResponse
} from '@modules/survey/types/objects';

import Analytics from './analytics/analytics';
import QuestionsEditor from './questions-editor';
import NameEditorModal from './name-editor-modal';
import SettingsEditor from './settings-editor';

import './edit-pulse-survey.scss';

type PulseSurveyTopNavBarProps = {
  survey: PulseSurveyResponse;
  orgId: string;
  setSurvey: Dispatch<SetStateAction<PulseSurveyResponse | null>>;
  onSubmit: () => void;
  loading: boolean;
};

const PulseSurveyTopNavBar = memo(({
  survey,
  orgId,
  setSurvey,
  onSubmit,
  loading = false,
}: PulseSurveyTopNavBarProps) => {
  const match = useRouteMatch();
  const status = survey?.data?.status;

  const statusOptions = useMemo(() => {
    return [{
      value: EStatus.DRAFT,
      label: t('common:status_draft')
    }, {
      value: EStatus.LIVE,
      label: t('common:status_live')
    }, {
      value: EPulseSurveyStatus.PAUSED,
      label: t('common:status_paused')
    }, {
      value: EStatus.ARCHIVED,
      label: t('common:status_archived')
    }];
  }, [t]);

  const onStatusChange = useCallback(async (selected: any) => {
    const url = `/v1/organisations/${orgId}/pulse_surveys/${survey.data.id}`;
    try {
      await Api.patch(url, { status: selected.value });
      AlertService.success(t('survey:form_survey_edited'));
      setSurvey((surv: any) => {
        return {
          ...surv,
          data: {
            ...surv?.data,
            status: selected.value
          }
        };
      });
    } catch (error: any) {
      AlertService.error(t('survey:survey_save_error'));
      throw error;
    }
  }, [orgId, survey, t, setSurvey]);

  const tabs = useMemo(() => {
    return [{
      name: t('survey:pulse_survey_tab_questions'),
      to: `${match.url}/questions`,
    }, {
      name: t('survey:survey_tabs_settings'),
      to: `${match.url}/settings`,
    }, (
      EStatus.DRAFT !== status ?
        {
          name: t('survey:pulse_survey_tab_analytics'),
          to: `${match.url}/analytics`,
        } :
        undefined
    )].filter((value: any) => !!value);
  }, [t, match, status]);

  return (
    <TopNavigationBar
      breadcrumbs={[
        {
          name: t('survey:pulse_surveys_sidebar_subtitle'),
          path: '/admin/surveys/pulse-overview'
        },
        { name: survey.data?.name }
      ]}
      form={Div}
      title={survey && (
        <NameEditorModal
          survey={survey}
          trigger={survey.data?.name}
          orgId={orgId}
          setSurvey={setSurvey}
        />
      )}
      badge={survey?.data?.status}
      tabs={tabs}
      action={(
        <>
          <Select
            clearable={false}
            className="PulseSurveyStatusSelector"
            options={statusOptions}
            value={survey?.data?.status}
            onChange={onStatusChange}
          />
          <Link to="/admin/surveys/pulse-overview">
            <Button size="large">
              {t('forms:go_back')}
            </Button>
          </Link>
          <Button
            type="primary"
            size="large"
            isLoading={loading}
            onClick={onSubmit}
            className="PulseSurveySaveButton"
          >
            <Trans i18nKey="common:save" />
          </Button>
        </>
      )}
    />
  );
});

const Div = memo(({ children }: PropsWithChildren<{}>) => {
  return <div>{children}</div>;
});

const useAnalyticsPredicates = (networks: any[], functions: any[]) => {
  return useState(() => {
    return [{
      filter: EPredicateFilters[EPredicateFields.DATE],
      value: {
        attribute: EPredicateFields.DATE,
        comparison: EPredicateOperators.BETWEEN,
        value: [moment().subtract(30, 'd').toDate(), new Date()]
      },
      options: undefined,
      scrolling: false
    }, {
      filter: EPredicateFilters[EPredicateFields.NETWORK],
      value: {
        attribute: EPredicateFields.NETWORK,
        comparison: EPredicateOperators.IN,
        value: []
      },
      options: networks,
    }, {
      filter: EPredicateFilters[EPredicateFields.FUNCTION],
      value: {
        attribute: EPredicateFields.FUNCTION,
        comparison: EPredicateOperators.IN,
        value: []
      },
      options: functions,
    }];
  });
};

const EditPulseSurvey = memo(() => {
  const match = useRouteMatch();

  const org = useSelector((s: StoreState) => orgSelectors.selected(s));
  const orgId = org.id;

  const networks = useSelector((s: StoreState) => orgSelectors.networks(s));
  const functions = useSelector((s: StoreState) => orgSelectors.functions(s));
  const [updating, setUpdating] = useState(false);

  const [analyticsPredicates, setAnalyticsPredicates] =
    useAnalyticsPredicates(networks, functions);

  // @ts-expect-error
  const surveyId = match?.params?.surveyId;

  // Fetch global questions
  const {
    data: globalQuestions,
    loading: globalQuestionsLoading,
  } = useRequest(`/v1/organisations/${orgId}/pulse_surveys/questions`);

  // Fetch custom questions
  const {
    data: customQuestions,
    loading: customQuestionsLoading,
    setData: setCustomQuestions
  } = useRequest(`/v1/organisations/${orgId}/pulse_surveys/questions`, {
    payload: {
      is_custom: true
    }
  });

  // Combine questions from both sources
  const [localQuestions, setLocalQuestions] = useState<any>(null);


  useEffect(() => {
    if (globalQuestions && customQuestions) {
      setLocalQuestions({
        data: [
          ...(globalQuestions || []),
          ...(customQuestions || [])
        ]
      });
    }
  }, [globalQuestions, customQuestions]);

  // Combined loading state
  const questionsLoading = globalQuestionsLoading || customQuestionsLoading;

  const questionsToRender = localQuestions || globalQuestions;

  const updateLocalQuestions = useCallback((updater: any) => {
    setLocalQuestions((currentQuestions: any) => {
      if (typeof updater === 'function') {
        return updater(currentQuestions);
      }
      return updater;
    });
  }, []);


  const { data: surveyData, loading: surveyLoading } = useRequest(`/v1/organisations/${orgId}/pulse_surveys/${surveyId}`, {
    transform: (result: any) => {
      return {
        data: { data: result.data },
      };
    }
  });

const [survey, setSurvey] = useState<null | PulseSurveyResponse>(null);

useEffect(() => {
  if (surveyData.data && !surveyLoading) {
    setSurvey(surveyData);
  }

}, [surveyData, surveyLoading]);

  const onSubmit = useCallback(async () => {
    if (!survey) return;

    const url = `/v1/organisations/${orgId}/pulse_surveys/${survey.data.id}`;
    try {
      setUpdating(true);
      await Api.patch(url, {
        settings: survey.data.settings,
        audience: {
          predicate_type: survey.data.audience.predicate_type,
          predicates: survey.data.audience.predicates?.map((pr: any) => {
            const clone = { ...pr };
            delete clone.id;
            return clone;
          })
        },
        questions: survey?.data?.questions?.map((q: PulseSurveyQuestionSelection) => {
          const question: any = {
            ...q,
            id: q.question_pool_id
          };
          delete question.pulse_survey_id;
          delete question.question_pool_id;
          delete question.created_at;
          return question;
        })
      });
      AlertService.success(t('survey:form_survey_edited'));
    } catch (error: any) {
      AlertService.error(t('survey:survey_save_error'));
      throw error;
    } finally {
      setUpdating(false);
    }
  }, [orgId, survey, setUpdating, t]);

  const loading = questionsLoading || !localQuestions || !survey;


  return (
    <Container name="EditPulseSurvey">
      {
        survey && (
          <PulseSurveyTopNavBar
            survey={survey}
            setSurvey={setSurvey}
            orgId={orgId}
            onSubmit={onSubmit}
            loading={updating}
          />
        )
      }
      {
        loading ? (
          <Spinner className="pageSpinner" centered size="large" />
        ) : (
          <Container.Content>
            <Route path={`${match.path}/questions`}>
              <QuestionsEditor
                questions={questionsToRender?.data || []}
                survey={survey}
                setSurvey={setSurvey}
                loading={updating || loading}
                orgId={orgId}
                surveyId={surveyId}
                setCustomQuestions={setCustomQuestions}
                updateLocalQuestions={updateLocalQuestions}
              />
            </Route>
            <Route path={`${match.path}/settings`}>
              <SettingsEditor
                survey={survey}
                setSurvey={setSurvey}
                orgId={orgId}
                loading={updating || loading}
              />
            </Route>
            <Route path={`${match.path}/analytics`}>
              <Analytics
                survey={survey}
                predicates={analyticsPredicates}
                setPredicates={setAnalyticsPredicates}
                organisation={org}
              />
            </Route>
          </Container.Content>
        )
      }
    </Container>
  );
});

export default EditPulseSurvey;
